import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Save,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ReceiptText,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPut } from '../lib/api'

type ConfigMap = Record<string, string>

/**
 * Claves de la facturación CFDI 4.0. Se leen/guardan en la tabla `config`
 * vía el mismo endpoint genérico que usa la página de Configuración:
 *   GET  /api/admin/config?keys=a,b,c   → { data: { a: '...', ... } }
 *   PUT  /api/admin/config { changes: { a: '...' } }
 */
const FACTURA_KEYS = [
  'Factura_Activo',
  'Factura_PAC_Provider',
  'Factura_PAC_Entorno',
  'Factura_PAC_ApiKey',
  'Factura_PAC_Secret',
  'Factura_Emisor_RFC',
  'Factura_Emisor_RazonSocial',
  'Factura_Emisor_Regimen',
  'Factura_Emisor_CP',
]

/** Catálogo SAT c_RegimenFiscal (CFDI 4.0). */
const REGIMEN_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Selecciona un régimen…' },
  { value: '601', label: '601 — General de Ley Personas Morales' },
  { value: '603', label: '603 — Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 — Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { value: '606', label: '606 — Arrendamiento' },
  { value: '607', label: '607 — Régimen de Enajenación o Adquisición de Bienes' },
  { value: '608', label: '608 — Demás ingresos' },
  { value: '610', label: '610 — Residentes en el Extranjero sin Establecimiento Permanente' },
  { value: '611', label: '611 — Ingresos por Dividendos (socios y accionistas)' },
  { value: '612', label: '612 — Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: '614', label: '614 — Ingresos por intereses' },
  { value: '615', label: '615 — Régimen de los ingresos por obtención de premios' },
  { value: '616', label: '616 — Sin obligaciones fiscales' },
  { value: '620', label: '620 — Sociedades Cooperativas de Producción' },
  { value: '621', label: '621 — Incorporación Fiscal' },
  { value: '622', label: '622 — Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: '623', label: '623 — Opcional para Grupos de Sociedades' },
  { value: '624', label: '624 — Coordinados' },
  { value: '625', label: '625 — Actividades Empresariales vía Plataformas Tecnológicas' },
  { value: '626', label: '626 — Régimen Simplificado de Confianza (RESICO)' },
]

const PROVIDER_OPTIONS: { value: string; label: string }[] = [
  { value: 'facturama', label: 'Facturama' },
]

function isTrue(v?: string): boolean {
  if (!v) return false
  const s = v.toLowerCase().trim()
  return s === '1' || s === 'true' || s === 'si' || s === 'sí' || s === 'on' || s === 'activo'
}

export function ConfiguracionFacturacion() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<ConfigMap>({})
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-config', FACTURA_KEYS.join(',')],
    queryFn: () =>
      apiGet<{ data: ConfigMap }>(`/api/admin/config?keys=${encodeURIComponent(FACTURA_KEYS.join(','))}`),
  })

  const original = data?.data ?? {}

  useEffect(() => {
    if (data?.data) {
      setDraft((prev) => (Object.keys(prev).length === 0 ? { ...data.data } : prev))
    }
  }, [data])

  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter((k) => (draft[k] ?? '') !== (original[k] ?? '')),
    [draft, original],
  )

  const saveMut = useMutation({
    mutationFn: async () => {
      const changes: ConfigMap = {}
      for (const k of dirtyKeys) changes[k] = draft[k] ?? ''
      return apiPut<{ updated: number; keys: string[] }>('/api/admin/config', { changes })
    },
    onSuccess: (r) => {
      setFlash({ tipo: 'ok', msg: `${r.updated} clave(s) guardadas` })
      qc.invalidateQueries({ queryKey: ['admin-config'] })
      setTimeout(() => setFlash(null), 3000)
    },
    onError: (e: any) => setFlash({ tipo: 'err', msg: e?.message || 'Error al guardar' }),
  })

  function set(k: string, v: string) {
    setDraft((d) => ({ ...d, [k]: v }))
  }
  function get(k: string): string {
    return draft[k] ?? ''
  }
  function toggleSecret(k: string) {
    setShowSecrets((s) => ({ ...s, [k]: !s[k] }))
  }
  function discard() {
    setDraft({ ...original })
  }

  const activo = isTrue(get('Factura_Activo'))
  const entorno = (get('Factura_PAC_Entorno') || 'sandbox').toLowerCase()
  const hasCreds = !!get('Factura_PAC_ApiKey').trim() && !!get('Factura_PAC_Secret').trim()
  const operativo = activo && hasCreds

  return (
    <>
      <Topbar
        title="Facturación"
        subtitle="Datos fiscales del emisor y credenciales del PAC (CFDI 4.0)"
        actions={
          <>
            {dirtyKeys.length > 0 && <Badge variant="warning">{dirtyKeys.length} sin guardar</Badge>}
            <button
              type="button"
              onClick={discard}
              disabled={dirtyKeys.length === 0}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={dirtyKeys.length === 0 || saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Guardar cambios
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ink-200 bg-white p-10 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando configuración…
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-6">
            {/* Toggle de activación + aviso */}
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600">
                  <ReceiptText className="size-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-ink-900">Facturación activa</h2>
                    <Badge variant={operativo ? 'success' : activo ? 'warning' : 'default'}>
                      {operativo ? 'operativa' : activo ? 'sin credenciales' : 'inactiva'}
                    </Badge>
                  </div>
                  <p className="text-xs text-ink-500">
                    Habilita el timbrado automático de las solicitudes de factura.
                  </p>
                </div>
                <Switch checked={activo} onChange={(v) => set('Factura_Activo', v ? '1' : '0')} />
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>
                  Mientras no esté activa y con credenciales válidas, las solicitudes se guardan pero{' '}
                  <strong>no se timbran</strong>. Quedan en estado "Pendiente" hasta que actives la
                  facturación y configures el PAC.
                </span>
              </div>
            </div>

            {/* Datos del emisor */}
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-ink-900">Datos fiscales del emisor</h2>
                <p className="text-xs text-ink-500">
                  Estos datos deben coincidir exactamente con la Constancia de Situación Fiscal del SAT.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label="RFC del emisor"
                  value={get('Factura_Emisor_RFC')}
                  onChange={(v) => set('Factura_Emisor_RFC', v.toUpperCase())}
                  placeholder="XAXX010101000"
                  mono
                />
                <Field
                  label="Código postal del domicilio fiscal"
                  value={get('Factura_Emisor_CP')}
                  onChange={(v) => set('Factura_Emisor_CP', v)}
                  placeholder="44100"
                  mono
                />
                <div className="sm:col-span-2">
                  <Field
                    label="Razón social"
                    value={get('Factura_Emisor_RazonSocial')}
                    onChange={(v) => set('Factura_Emisor_RazonSocial', v)}
                    placeholder="MAZ AUTOPARTES SA DE CV"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Select
                    label="Régimen fiscal"
                    value={get('Factura_Emisor_Regimen')}
                    onChange={(v) => set('Factura_Emisor_Regimen', v)}
                    options={REGIMEN_OPTIONS}
                  />
                </div>
              </div>
            </div>

            {/* PAC / Credenciales */}
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-ink-900">Proveedor PAC (timbrado)</h2>
                <p className="text-xs text-ink-500">
                  Credenciales del PAC que ejecuta el timbrado fiscal del CFDI.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Select
                  label="Proveedor"
                  value={get('Factura_PAC_Provider') || 'facturama'}
                  onChange={(v) => set('Factura_PAC_Provider', v)}
                  options={PROVIDER_OPTIONS}
                />
                <Select
                  label="Entorno"
                  value={entorno}
                  onChange={(v) => set('Factura_PAC_Entorno', v)}
                  options={[
                    { value: 'sandbox', label: 'Sandbox (pruebas)' },
                    { value: 'live', label: 'Live (producción)' },
                  ]}
                />
                <Field
                  label="API Key / Usuario"
                  value={get('Factura_PAC_ApiKey')}
                  onChange={(v) => set('Factura_PAC_ApiKey', v)}
                  mono
                />
                <SecretField
                  label="Secret / Contraseña"
                  value={get('Factura_PAC_Secret')}
                  onChange={(v) => set('Factura_PAC_Secret', v)}
                  visible={!!showSecrets['Factura_PAC_Secret']}
                  onToggle={() => toggleSecret('Factura_PAC_Secret')}
                />
              </div>
              {entorno === 'sandbox' && (
                <p className="mt-3 text-[11px] text-ink-500">
                  En entorno <span className="font-mono">sandbox</span> los CFDI son de prueba y no
                  tienen validez fiscal. Cambia a <span className="font-mono">live</span> con
                  credenciales de producción para emitir facturas reales.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ---------- campos auxiliares (mismo estilo que Configuracion.tsx) ---------- */

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  mono = false,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  mono?: boolean
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 ${
          mono ? 'font-mono text-xs' : ''
        }`}
      />
    </div>
  )
}

function SecretField({
  label,
  value,
  onChange,
  visible,
  onToggle,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  visible: boolean
  onToggle: () => void
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 pr-9 font-mono text-xs outline-none focus:border-brand-500"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-700"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  )
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function Switch({
  label,
  checked,
  onChange,
}: {
  label?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <span className="relative inline-block h-6 w-11">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="absolute inset-0 rounded-full bg-ink-200 transition-colors peer-checked:bg-brand-600" />
        <span className="absolute left-0.5 top-0.5 size-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
      </span>
      {label && <span className="text-sm text-ink-700">{label}</span>}
    </label>
  )
}
